import React, { useState } from 'react';

// Provider tipi — backend ile aynı sirayi takip ediyor (auth.service.ts)
export type AvatarProvider = 'github' | 'google' | 'linkedin' | null | undefined;

interface UserAvatarProps {
  // OAuth/CDN profil resmi URL'i; null/undefined olursa initial'a fallback
  src?: string | null;
  // Initial'lar icin ad; bos ise "?" gosteririz
  name?: string | null;
  // Erisilebilirlik
  email?: string | null;
  // Tailwind boyut sinifi; varsayilan w-9 h-9
  sizeClass?: string;
  // Provider rozeti goster (sag-alt kose)
  showProviderBadge?: boolean;
  provider?: AvatarProvider;
  // Ekstra class (border, ring vs.)
  className?: string;
}

// Adin bas harflerini al — "Onur Ozdemir" -> "OO", "onur" -> "O"
function getInitials(name?: string | null, email?: string | null): string {
  const source = (name && name.trim()) || (email && email.split('@')[0]) || '';
  if (!source) return '?';
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Provider'a gore deterministik renk gradient — initial avatar arka plani
function getGradientFor(seed: string): string {
  // Basit hash — string -> int
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const palettes = [
    'from-purple-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-emerald-500 to-teal-500',
    'from-amber-500 to-orange-500',
    'from-rose-500 to-red-500',
    'from-indigo-500 to-violet-500',
  ];
  return palettes[Math.abs(hash) % palettes.length];
}

function ProviderBadge({ provider, sizeClass }: { provider: AvatarProvider; sizeClass: string }) {
  if (!provider) return null;
  // Badge boyutu ana avatar'in yaklasik 1/3'u — w-9 -> w-3.5 gibi
  // Sabit kucuk boyut kullaniyoruz cunku ana boyut çesitli olabilir
  const badgeSize = 'w-4 h-4';
  const bg: Record<Exclude<AvatarProvider, null | undefined>, string> = {
    github: 'bg-gray-900',
    google: 'bg-white',
    linkedin: 'bg-[#0a66c2]',
  };
  return (
    <span
      className={`absolute -bottom-0.5 -right-0.5 ${badgeSize} ${bg[provider]} rounded-full flex items-center justify-center ring-2 ring-slate-900 shadow`}
      title={`${provider[0].toUpperCase()}${provider.slice(1)} ile bagli`}
      aria-label={`${provider} ile bagli`}
    >
      {provider === 'github' && (
        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-white" aria-hidden="true">
          <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.56v-2c-3.2.7-3.88-1.37-3.88-1.37-.52-1.34-1.28-1.69-1.28-1.69-1.04-.71.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.96.1-.74.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.7 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11.06 11.06 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.12 3.05.74.81 1.18 1.84 1.18 3.1 0 4.43-2.69 5.41-5.25 5.69.41.36.78 1.06.78 2.15v3.18c0 .31.21.67.8.56C20.21 21.39 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5z" />
        </svg>
      )}
      {provider === 'google' && (
        <svg viewBox="0 0 48 48" className="w-2.5 h-2.5" aria-hidden="true">
          <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
          <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
          <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.991 21.991 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
          <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z" />
        </svg>
      )}
      {provider === 'linkedin' && (
        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-white" aria-hidden="true">
          <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.21 0 22.23 0z" />
        </svg>
      )}
    </span>
  );
}

export default function UserAvatar({
  src,
  name,
  email,
  sizeClass = 'w-9 h-9',
  showProviderBadge = false,
  provider,
  className = '',
}: UserAvatarProps) {
  const [imgError, setImgError] = useState(false);
  const showImage = src && !imgError;

  return (
    <span className={`relative inline-flex ${className}`}>
      {showImage ? (
        <img
          src={src as string}
          alt={name || email || 'Kullanici'}
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setImgError(true)}
          className={`${sizeClass} rounded-full object-cover ring-1 ring-white/10 bg-slate-800`}
        />
      ) : (
        <span
          className={`${sizeClass} rounded-full bg-gradient-to-br ${getGradientFor(
            name || email || 'u'
          )} text-white font-semibold flex items-center justify-center select-none`}
          aria-label={name || email || 'Kullanici'}
        >
          <span className="text-[0.85em] leading-none">
            {getInitials(name, email)}
          </span>
        </span>
      )}
      {showProviderBadge && <ProviderBadge provider={provider} sizeClass={sizeClass} />}
    </span>
  );
}
